import type {
  ShareLink,
  SharedTripApi,
  TripDetails,
  TripPosition,
  TripStatus,
  TripVisibility,
} from './types'
import { SharedTripApiError } from './types'

type JsonRecord = Record<string, unknown>

const defaultEndpoint = 'https://www.mapapal.com/Com/Com.aspx'

export interface LegacyMapapalApiOptions {
  baseUrl?: string
  fetchImpl?: typeof fetch
}

function record(value: unknown): JsonRecord | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? value as JsonRecord
    : null
}

function oneOrMany(value: unknown): JsonRecord[] {
  if (Array.isArray(value)) return value.map(record).filter((item): item is JsonRecord => item !== null)
  const item = record(value)
  return item ? [item] : []
}

function text(value: unknown): string {
  return value == null ? '' : String(value)
}

function number(value: unknown, fallback = 0): number {
  const parsed = Number.parseFloat(text(value).replace(',', '.'))
  return Number.isFinite(parsed) ? parsed : fallback
}

function visibility(value: unknown): TripVisibility {
  if (text(value) === '0') return 'private'
  if (text(value) === '1' || text(value) === '2') return 'pals'
  if (text(value) === '3') return 'public'
  return 'unknown'
}

function status(value: unknown): TripStatus {
  const candidate = text(value)
  return candidate === 'running' || candidate === 'paused' ? candidate : 'ended'
}

function normalizePosition(value: JsonRecord): TripPosition | null {
  const latitude = number(value.lat, Number.NaN)
  const longitude = number(value.lng, Number.NaN)
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return null

  const optional = (input: unknown) => {
    const parsed = number(input, Number.NaN)
    return Number.isFinite(parsed) ? parsed : undefined
  }

  return {
    id: text(value.DBID),
    latitude,
    longitude,
    recordedAt: text(value.t_added),
    altitudeMeters: optional(value.alt),
    accuracyMeters: optional(value.acc),
    speedMetersPerSecond: optional(value.spe),
    bearingDegrees: optional(value.bea),
    distanceKilometers: optional(value.tot_km),
    comment: text(value.c).trim() || undefined,
  }
}

function normalizeTrip(value: JsonRecord, positions: TripPosition[]): TripDetails {
  const views = number(value.views, Number.NaN)
  return {
    id: text(value.trip_id),
    ownerName: text(value.username) || 'Mapapal user',
    description: text(value.desc) || undefined,
    status: status(value.status),
    visibility: visibility(value.sharestatus_id),
    startedAt: text(value.t_start),
    endedAt: text(value.t_stop) || undefined,
    durationSeconds: number(value.tot_s),
    distanceKilometers: number(value.dist_km),
    averageSpeedKilometersPerHour: number(value.avgkmh),
    platform: text(value.platform).trim() || undefined,
    views: Number.isFinite(views) ? Math.max(0, Math.trunc(views)) : undefined,
    positions,
  }
}

function positionsFromPayload(payload: JsonRecord, allowNoPositions = false): TripPosition[] {
  const ret = record(payload.ret)
  if (text(ret?.status) !== 'ok') {
    if (allowNoPositions && /no positions/i.test(text(ret?.msg))) return []
    throw new SharedTripApiError(
      /access/i.test(text(ret?.msg)) ? 'access-denied' : 'invalid-response',
      'The route could not be loaded.',
    )
  }
  const container = record(ret?.positions)
  return oneOrMany(container?.pos)
    .map(normalizePosition)
    .filter((position): position is TripPosition => position !== null)
}

export class LegacyMapapalApi implements SharedTripApi {
  private readonly endpoint: string
  private readonly fetchImpl: typeof fetch

  constructor(options: LegacyMapapalApiOptions = {}) {
    this.endpoint = options.baseUrl ?? defaultEndpoint
    this.fetchImpl = options.fetchImpl ?? ((input, init) => fetch(input, init))
  }

  async getSharedTrip(link: ShareLink, signal?: AbortSignal): Promise<TripDetails> {
    const list = await this.request({
      sid: '',
      f: 'trip_list',
      v_m: '1111',
      fritext: link.tripId,
      t_id: link.tripId,
      uno: link.userNo,
      rc: '15',
    }, signal)

    const ret = record(list.ret)
    const trips = record(ret?.trips)
    const trip = oneOrMany(trips?.trip).find((item) => text(item.trip_id) === link.tripId)
    if (!trip) {
      throw new SharedTripApiError(
        'not-found-or-denied',
        'Trip not found or access was denied.',
      )
    }

    const positionsPayload = await this.request({
      sid: '',
      f: 'trip_getpositions',
      t_id: link.tripId,
      uno: link.userNo,
    }, signal)
    return normalizeTrip(trip, positionsFromPayload(positionsPayload))
  }

  async refreshSharedTrip(
    link: ShareLink,
    lastPositionId: string,
    signal?: AbortSignal,
  ): Promise<TripDetails> {
    const [tripPayload, positionsPayload] = await Promise.all([
      this.request({
        sid: '',
        f: 'trip_get',
        t_id: link.tripId,
        uno: link.userNo,
      }, signal),
      this.request({
        sid: '',
        f: 'trip_getpositions',
        t_id: link.tripId,
        uno: link.userNo,
        last_id: lastPositionId || '0',
      }, signal),
    ])
    const tripRet = record(tripPayload.ret)
    const trips = record(tripRet?.trips)
    const trip = oneOrMany(trips?.trip).find((item) => text(item.trip_id) === link.tripId)
    if (text(tripRet?.status) !== 'ok' || !trip) {
      throw new SharedTripApiError('not-found-or-denied', 'Trip not found or access was denied.')
    }
    return normalizeTrip(trip, positionsFromPayload(positionsPayload, true))
  }

  async getSharedTripCsv(link: ShareLink, signal?: AbortSignal): Promise<Blob> {
    const query = new URLSearchParams({
      sid: '',
      f: 'trip_save',
      t_id: link.tripId,
      uno: link.userNo,
    })
    let response: Response
    try {
      response = await this.fetchImpl(`${this.endpoint}?web&${query}`, {
        method: 'GET',
        headers: { Accept: 'text/csv,application/octet-stream' },
        signal,
      })
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error
      throw new SharedTripApiError('network', 'The Mapapal service could not be reached.', { cause: error })
    }
    if (!response.ok) {
      throw new SharedTripApiError('network', `The Mapapal service returned HTTP ${response.status}.`)
    }
    const blob = await response.blob()
    if (blob.size === 0) throw new SharedTripApiError('invalid-response', 'Mapapal returned an empty CSV file.')
    return blob
  }

  private async request(params: Record<string, string>, signal?: AbortSignal): Promise<JsonRecord> {
    const query = new URLSearchParams(params)
    let response: Response
    try {
      response = await this.fetchImpl(`${this.endpoint}?mobweb&${query}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
        signal,
      })
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw error
      }
      throw new SharedTripApiError('network', 'The Mapapal service could not be reached.', { cause: error })
    }

    if (!response.ok) {
      throw new SharedTripApiError('network', `The Mapapal service returned HTTP ${response.status}.`)
    }

    try {
      return await response.json() as JsonRecord
    } catch (error) {
      throw new SharedTripApiError('invalid-response', 'The Mapapal service returned invalid JSON.', { cause: error })
    }
  }
}
